import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, CheckCircle2, RotateCcw, Reply, Loader2, Image as ImageIcon } from "lucide-react";
import api from "../lib/api";
import { CATEGORIES } from "./ReportSheet";

export default function ReportInbox() {
  const [reports, setReports] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const load = async () => { const { data } = await api.get("/admin/reports"); setReports(data); };
  useEffect(() => { load().catch(() => {}); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/reports/${id}`, { status });
    toast.success(status === "resolved" ? "Marked resolved" : "Reopened");
    load();
  };
  const [replying, setReplying] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const sendReply = async (id) => {
    if (draft.trim().length < 2) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/reports/${id}/reply`, { message: draft.trim(), resolve: true });
      toast.success(data.emailed ? "Reply sent as Millie and marked resolved" : "Saved, but the email didn't send");
      setReplying(null); setDraft(""); load();
    } catch { toast.error("Couldn't send that reply"); }
    finally { setSending(false); }
  };
  const visible = reports.filter((r) => showResolved || r.status === "open");
  const openCount = reports.filter((r) => r.status === "open").length;

  return (
    <section className="mt-6" data-testid="report-inbox">
      <div className="flex items-center justify-between px-1">
        <h2 className="font-hand text-2xl text-sorted-ink flex items-center gap-2"><Inbox size={20} strokeWidth={2.5} /> Problem reports {openCount > 0 && <span className="text-xs font-bold bg-sorted-rose text-white rounded-full px-2 py-0.5" data-testid="report-open-count">{openCount}</span>}</h2>
        <button onClick={() => setShowResolved((v) => !v)} className="text-xs font-bold text-sorted-slate underline" data-testid="report-toggle-resolved">{showResolved ? "Hide resolved" : "Show resolved"}</button>
      </div>
      <div className="mt-3 space-y-2">
        {visible.length === 0 && <p className="text-sm font-semibold text-sorted-slate bg-white rounded-3xl px-4 py-3 border-2 border-[#FDECEF]" data-testid="report-inbox-empty">Nothing open — ripper.</p>}
        {visible.map((r) => (
          <article key={r.id} className={`bg-white rounded-3xl border-2 border-[#FDECEF] p-4 ${r.status === "resolved" ? "opacity-60" : ""}`} data-testid={`report-item-${r.id}`}>
            <div className="flex items-center gap-2 text-xs font-bold text-sorted-slate">
              <span className="bg-muted text-sorted-ink rounded-full px-2 py-0.5">{CATEGORIES.find((c) => c.id === r.category)?.label || r.category}</span>
              <span className="truncate">{r.user_name} · {r.user_email}</span>
            </div>
            <p className="mt-2 text-sm font-medium text-sorted-ink whitespace-pre-wrap">{r.message}</p>
            {r.context?.label && <p className="mt-1 text-xs font-semibold text-sorted-slate truncate">About: {r.context.label}</p>}
            {r.screenshot_id && <ReportShot id={r.screenshot_id} />}
            {r.replies?.map((rp, i) => <p key={i} className="mt-2 text-xs font-medium text-sorted-ink bg-sorted-sage/40 rounded-2xl px-3 py-2 whitespace-pre-wrap" data-testid={`report-reply-${r.id}-${i}`}><span className="font-bold">Millie replied:</span> {rp.message}</p>)}
            {replying === r.id ? (
              <div className="mt-2" data-testid={`report-reply-box-${r.id}`}>
                <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} maxLength={3000} placeholder="Write as Millie — warm, short, what you did about it" data-testid="report-reply-text"
                  className="w-full rounded-2xl border-2 border-[#FDECEF] px-3 py-2 text-sm font-medium focus:outline-none focus:border-sorted-rose" />
                <div className="mt-1.5 flex gap-2 justify-end">
                  <button onClick={() => { setReplying(null); setDraft(""); }} className="text-xs font-bold text-sorted-slate px-3 py-1.5" data-testid="report-reply-cancel">Cancel</button>
                  <button onClick={() => sendReply(r.id)} disabled={sending} className="text-xs font-bold bg-sorted-ink text-white rounded-full px-4 py-1.5 flex items-center gap-1 disabled:opacity-60" data-testid="report-reply-send">{sending ? <Loader2 size={12} className="animate-spin" /> : <Reply size={12} strokeWidth={2.5} />} Send & resolve</button>
                </div>
              </div>
            ) : null}
            <div className="mt-2 flex items-center justify-between text-xs font-semibold text-sorted-slate">
              <span>{r.page} · {new Date(r.created_at).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</span>
              {r.status === "open"
                ? <span className="flex items-center gap-3">
                    <button onClick={() => { setReplying(r.id); setDraft(""); }} className="flex items-center gap-1 text-sorted-rose font-bold" data-testid={`report-reply-${r.id}`}><Reply size={14} strokeWidth={2.5} /> Reply</button>
                    <button onClick={() => setStatus(r.id, "resolved")} className="flex items-center gap-1 text-sorted-ink font-bold" data-testid={`report-resolve-${r.id}`}><CheckCircle2 size={14} strokeWidth={2.5} /> Resolve</button>
                  </span>
                : <button onClick={() => setStatus(r.id, "open")} className="flex items-center gap-1 font-bold" data-testid={`report-reopen-${r.id}`}><RotateCcw size={14} strokeWidth={2.5} /> Reopen</button>}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function ReportShot({ id }) {
  const [src, setSrc] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let url;
    api.get(`/reports/screenshot/${id}`, { responseType: "blob" }).then(({ data }) => { url = URL.createObjectURL(data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [id]);
  if (!src) return <p className="mt-1 text-xs font-semibold text-sorted-slate flex items-center gap-1"><ImageIcon size={12} /> Screenshot attached</p>;
  return (<>
    <img src={src} alt="Report screenshot" onClick={() => setOpen(true)} className="mt-2 h-24 rounded-xl border-2 border-[#FDECEF] object-cover cursor-zoom-in" data-testid="report-screenshot-thumb" />
    {open && <div className="fixed inset-0 z-[80] bg-sorted-ink/80 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="report-screenshot-full"><img src={src} alt="Report screenshot" className="max-h-full max-w-full rounded-2xl" /></div>}
  </>);
}
